import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompetencyTypeClient, CompetencyTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new CompetencyTypeClient(API_BASE_URL, axiosInstance);

// Query hook - Get all competency types for dropdowns (cached forever)
export const useCompetencyTypes = () => {
  return useQuery({
    queryKey: ['competencyTypes'],
    queryFn: async () => {
      const response = await apiClient.searchCompetencyTypes({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useCompetencyTypeSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchCompetencyTypes({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single competency type by ID
export const useCompetencyTypeById = (id: string) => {
  return useQuery({
    queryKey: ['competencyTypes', id],
    queryFn: async () => {
      const response = await apiClient.getCompetencyTypeById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create competency type
export const useCreateCompetencyType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CompetencyTypeDTO) => {
      await apiClient.createCompetencyType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencyTypes'] });
    },
  });
};

// Mutation hook - Update competency type
export const useUpdateCompetencyType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CompetencyTypeDTO }) => {
      await apiClient.updateCompetencyType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencyTypes'] });
    },
  });
};

// Mutation hook - Delete competency type
export const useDeleteCompetencyType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteCompetencyType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competencyTypes'] });
    },
  });
};

// Legacy hook for backward compatibility (deprecated - use the hooks above)
export const useCompetencyTypeApi = () => {
    const apiClient = new CompetencyTypeClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: CompetencyTypeDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createCompetencyType(data);
        } catch (err: any) {
            setError(err.message || 'Error creating CompetencyType');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: CompetencyTypeDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateCompetencyType(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating CompetencyType');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getCompetencyTypeById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching CompetencyType');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchCompetencyTypes(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching CompetencyTypes');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteCompetencyType(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting CompetencyType');
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, remove, isLoading, error };
};